/**
 * <bb-set-builder> — starter set builder
 *
 * Replaces the third-party bundle app. The customer picks a preset, or opens "Anpassen" and changes
 * the quantities the preset is made of; everything is added to the cart in a single /cart/add.js call:
 *
 *   - wrapper        -> parent line at 0.00, carries the set's name
 *   - accessory set  -> nested child line (parent_id)
 *   - matcha bags    -> nested child lines, so cart/checkout/order show one group
 *   - gift whisk     -> child line at price 0, added on its own from `gift.threshold` bags
 *
 * Prices shown here are projections. The real quantity discount comes from Shopify automatic
 * discounts on the matcha collection, the accessory/gift savings come from the variant prices
 * themselves. Nothing here enforces anything server-side — see bb-cart-gift.js for the cart guard.
 *
 * Config arrives as JSON in a <script type="application/json" data-bb="config"> child so the
 * section can pass arrays (sorts, presets) without dozens of data-* attributes.
 */
// The last tier the meter shows; the section renders three segments for it.
const MAX_TIER_BAGS = 3;

class BbSetBuilder extends HTMLElement {
  connectedCallback() {
    const configEl = this.querySelector('[data-bb="config"]');
    if (!configEl) return;

    try {
      this.config = BbSetBuilder.decodeEntities(JSON.parse(configEl.textContent));
    } catch (error) {
      console.error('[bb-set-builder] invalid config', error);
      return;
    }

    const { sorts = [], presets = [], defaultPreset = 0 } = this.config;
    this.sorts = sorts;
    this.presets = presets;

    const deepLinked = this.presetIndexFromUrl();
    this.deepLinked = deepLinked >= 0;
    const preset = this.presets[deepLinked < 0 ? defaultPreset : deepLinked] || this.presets[0];
    this.state = {
      qty: this.qtyFromPreset(preset),
      accessory: true,
      // Same rule as the panel's "auto" CSS, which is what the page was rendered with.
      open: window.matchMedia('(min-width: 1000px)').matches,
      added: false,
      busy: false,
      error: '',
    };

    this.maxTierReached = this.bagCount() >= MAX_TIER_BAGS;

    this.cacheNodes();
    this.bindEvents();
    this.renderPresetPrices();
    this.watchBar();
    this.startCutoffCountdown();
    this.renderDelivery();
    this.render();
  }

  /**
   * A link can preselect a preset on the generic set page (?preset=signature, or ?set=signature),
   * which is what makes an ad or a collection tile land on the configuration it advertised. The
   * preset landing pages carry the same information in a metafield instead, because they need it
   * server-side.
   */
  presetIndexFromUrl() {
    const params = new URLSearchParams(window.location.search);
    const wanted = params.get('preset') || params.get('set');
    if (!wanted) return -1;
    const key = wanted.trim().toLowerCase();
    return this.presets.findIndex((preset) => {
      const id = String(preset.id || '').toLowerCase();
      return id === key || id === `preset_${key}`;
    });
  }

  disconnectedCallback() {
    if (this._addedTimer) clearTimeout(this._addedTimer);
    if (this._cutoffTicker) clearInterval(this._cutoffTicker);
    if (this._confettiFrame) cancelAnimationFrame(this._confettiFrame);
    if (this._revealTimer) clearTimeout(this._revealTimer);
    if (this._onViewportChange) {
      window.removeEventListener('scroll', this._onViewportChange);
      window.removeEventListener('resize', this._onViewportChange);
    }
  }

  /**
   * The price bar has a place under the builder, and CSS keeps it on screen for as long as that
   * place is still ahead (position: sticky). Once the place has scrolled out at the top there is
   * nothing CSS can do — a sticky element can't follow the page out of its own section — so from
   * there the bar is pinned to the bottom of the screen by a class, and released again the moment
   * its place comes back into view.
   *
   * The place keeps its height while the bar is away, so nothing below it moves. Whether "pinned"
   * shows at all is the stylesheet's call (it only does below the two-column layout).
   */
  watchBar() {
    const place = this.nodes.bar;
    if (!place) return;

    const header = document.querySelector('.shopify-section--header');
    let queued = false;

    this.updateBar = () => {
      queued = false;
      // Out of sight means behind the sticky header, not only past the top of the window.
      const ceiling = header ? Math.max(0, header.getBoundingClientRect().bottom) : 0;
      const detached = place.getBoundingClientRect().bottom <= ceiling;
      if (detached === this._barDetached) return;

      this._barDetached = detached;
      place.style.minHeight = detached ? `${place.offsetHeight}px` : '';
      place.classList.toggle('is-detached', detached);
    };

    this._onViewportChange = () => {
      if (queued) return;
      queued = true;
      requestAnimationFrame(this.updateBar);
    };

    window.addEventListener('scroll', this._onViewportChange, { passive: true });
    window.addEventListener('resize', this._onViewportChange);
    this.updateBar();
  }

  /**
   * Time left to today's dispatch cut-off, and the date that buys — the same promise
   * bb-pdp-single-matcha makes, so a set doesn't read as slower to ship than a single bag.
   */
  startCutoffCountdown() {
    const node = this.nodes.countdown;
    if (!node) return;

    const cutoffHour = Number(this.config.cutoffHour) || 16;

    const tick = () => {
      const now = new Date();
      const cutoff = new Date(now);
      cutoff.setHours(cutoffHour, 0, 0, 0);
      if (cutoff <= now) cutoff.setDate(cutoff.getDate() + 1);

      const seconds = Math.max(0, Math.floor((cutoff - now) / 1000));
      const hours = Math.floor(seconds / 3600);
      const minutes = Math.floor((seconds % 3600) / 60);
      const parts = [];
      if (hours > 0) parts.push(`${hours} ${this.t('unit_hours')}`);
      parts.push(`${minutes} ${this.t('unit_minutes')}`);
      node.textContent = parts.join(' ');
    };

    tick();
    this._cutoffTicker = setInterval(tick, 20000);
  }

  renderDelivery() {
    const node = this.nodes.delivery;
    if (!node) return;

    const date = new Date();
    let workdays = 0;
    while (workdays < 3) {
      date.setDate(date.getDate() + 1);
      const day = date.getDay();
      if (day !== 0 && day !== 6) workdays++;
    }

    try {
      node.textContent = new Intl.DateTimeFormat(this.config.locale || undefined, {
        weekday: 'long',
        day: 'numeric',
        month: 'short',
      }).format(date);
    } catch (error) {
      node.textContent = date.toLocaleDateString();
    }
  }

  /**
   * Liquid's `t` filter HTML-escapes its output, and entities inside a <script type="application/json">
   * are not decoded by the parser — so "Tu l'as merite" would reach us as "Tu l&#39;as merite".
   * Decode every string in the config once, on the way in.
   */
  static decodeEntities(value) {
    if (typeof value === 'string') {
      if (!value.includes('&')) return value;
      const textarea = document.createElement('textarea');
      textarea.innerHTML = value;
      return textarea.value;
    }
    if (Array.isArray(value)) return value.map(BbSetBuilder.decodeEntities);
    if (value && typeof value === 'object') {
      Object.keys(value).forEach((key) => {
        value[key] = BbSetBuilder.decodeEntities(value[key]);
      });
    }
    return value;
  }

  qtyFromPreset(preset) {
    const qty = {};
    this.sorts.forEach((sort) => {
      const wanted = preset && preset.qty ? preset.qty[sort.variantId] || 0 : 0;
      // A preset that contains a sold-out sort still works — it just arrives without that bag,
      // which beats letting the customer build a set that add.js will reject wholesale.
      qty[sort.variantId] = sort.available === false ? 0 : wanted;
    });
    return qty;
  }

  cacheNodes() {
    this.nodes = {
      presets: Array.from(this.querySelectorAll('[data-bb="preset"]')),
      widget: this.querySelector('.bb-set-builder__widget'),
      meterBox: this.querySelector('[data-bb="meter"]'),
      meterSegments: Array.from(this.querySelectorAll('[data-bb="meter-seg"]')),
      meterTitle: this.querySelector('[data-bb="meter-title"]'),
      meterHint: this.querySelector('[data-bb="meter-hint"]'),
      meterAdd: this.querySelector('[data-bb="meter-add"]'),
      summary: this.querySelector('[data-bb="summary"]'),
      customize: this.querySelector('[data-bb="customize"]'),
      bar: this.querySelector('[data-bb="bar"]'),
      rowsPanel: this.querySelector('[data-bb="rows-panel"]'),
      matchaRows: Array.from(this.querySelectorAll('[data-bb="matcha-row"]')),
      accRow: this.querySelector('[data-bb="acc-row"]'),
      accToggle: this.querySelector('[data-bb="acc-toggle"]'),
      total: this.querySelector('[data-bb="total"]'),
      compare: this.querySelector('[data-bb="compare"]'),
      cta: this.querySelector('[data-bb="cta"]'),
      error: this.querySelector('[data-bb="error"]'),
      pageTitle: this.querySelector('[data-bb="page-title"]'),
      countdown: this.querySelector('[data-bb="countdown"]'),
      delivery: this.querySelector('[data-bb="delivery"]'),
    };
  }

  bindEvents() {
    this.nodes.presets.forEach((card) => {
      card.addEventListener('click', () => {
        const preset = this.presets[parseInt(card.dataset.presetIndex, 10)];
        if (!preset) return;
        // The rows stay as they are, open or closed: someone fine-tuning shouldn't have the
        // list folded away because they tried another starting point.
        this.setState({ qty: this.qtyFromPreset(preset), accessory: true });
      });
    });

    if (this.nodes.customize) {
      this.nodes.customize.addEventListener('click', () => {
        this.state.open = !this.state.open;
        this.renderPanel();
      });
    }

    if (this.nodes.meterAdd) {
      this.nodes.meterAdd.addEventListener('click', () => this.revealRows());
    }

    if (this.nodes.accToggle) {
      this.nodes.accToggle.addEventListener('click', () => {
        if (this.state.accessory && !this.accessoryRemovable()) return;
        this.setState({ accessory: !this.state.accessory });
      });
    }

    this.nodes.matchaRows.forEach((row) => {
      const variantId = row.dataset.variantId;
      const inc = row.querySelector('[data-bb="inc"]');
      const dec = row.querySelector('[data-bb="dec"]');

      const change = (delta) => {
        const sort = this.sorts.find((item) => String(item.variantId) === String(variantId));
        if (sort && sort.available === false) return;
        const next = Math.max(0, Math.min(this.config.maxPerSort || 9, (this.state.qty[variantId] || 0) + delta));
        this.setState({ qty: { ...this.state.qty, [variantId]: next } });
      };

      if (inc) inc.addEventListener('click', () => change(1));
      if (dec) dec.addEventListener('click', () => change(-1));
    });

    if (this.nodes.cta) this.nodes.cta.addEventListener('click', () => this.addToCart());
  }

  setState(patch) {
    Object.assign(this.state, patch, { added: false, error: '' });
    this.render();
  }

  // ---- pricing ----

  bagCount() {
    return this.sorts.reduce((sum, sort) => sum + (this.state.qty[sort.variantId] || 0), 0);
  }

  /** Tier rate as a fraction. Mirrors the Shopify automatic discounts; display only. */
  tierRate(bags) {
    const { tier2 = 0, tier3 = 0 } = this.config;
    if (bags >= 3) return tier3 / 100;
    if (bags === 2) return tier2 / 100;
    return 0;
  }

  /**
   * Shopify allocates the discount per unit and truncates to whole cents
   * (36.90 at 15% -> 5.53, not 5.54), so mirror that here or the projection drifts.
   */
  unitDiscount(price, rate) {
    return rate > 0 ? Math.floor(price * rate) : 0;
  }

  /**
   * The gift is not in here on purpose: it costs nothing, and counting its list price into "was"
   * would quote a saving on something the buyer never set out to pay for.
   */
  totals(qty = this.state.qty, accessory = this.state.accessory) {
    const bags = this.sorts.reduce((sum, sort) => sum + (qty[sort.variantId] || 0), 0);
    const rate = this.tierRate(bags);

    let matchaNow = 0;
    let matchaWas = 0;
    this.sorts.forEach((sort) => {
      const count = qty[sort.variantId] || 0;
      if (!count) return;
      matchaWas += sort.price * count;
      matchaNow += (sort.price - this.unitDiscount(sort.price, rate)) * count;
    });

    const accessory_ = accessory ? this.config.accessory : null;
    const accNow = accessory_ ? accessory_.price : 0;
    const accWas = accessory_ ? accessory_.compareAtPrice || accessory_.price : 0;

    return {
      bags,
      rate,
      now: matchaNow + accNow,
      was: matchaWas + accWas,
    };
  }

  formatMoney(cents) {
    const amount = (cents / 100).toFixed(2);
    return (this.config.moneyFormat || 'CHF {{amount}}')
      .replace(/\{\{\s*amount(_no_decimals)?\s*\}\}/g, amount)
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, amount.replace('.', ','));
  }

  /** Placeholders arrive as __token__ because {{ }} inside a Liquid filter argument closes the tag early. */
  t(key, replacements = {}) {
    let string = (this.config.i18n || {})[key] || '';
    Object.keys(replacements).forEach((token) => {
      string = string.split(`__${token}__`).join(replacements[token]);
    });
    return string;
  }

  giftThreshold() {
    return this.config.gift ? this.config.gift.threshold : Infinity;
  }

  giftUnlocked() {
    return Boolean(this.config.gift) && this.bagCount() >= this.giftThreshold();
  }

  accessoryRemovable() {
    return this.config.accessoryRemovable !== false;
  }

  // ---- render ----

  render() {
    const { bags, rate, now, was } = this.totals();

    this.renderPresets();
    this.renderMeter(bags, rate);
    this.renderSummary();
    this.renderPanel();
    this.renderAccessory();
    this.renderMatchaRows(rate);
    this.renderPageTitle();

    if (this.nodes.total) this.nodes.total.textContent = this.formatMoney(now);

    // With no bags chosen the set isn't purchasable, so quoting a saving on it would be a promise
    // about a cart the customer can't create.
    const showSaving = was - now > 0 && bags > 0;
    if (this.nodes.compare) {
      this.nodes.compare.textContent = showSaving ? this.formatMoney(was) : '';
      this.nodes.compare.hidden = !showSaving;
    }

    if (this.nodes.error) {
      this.nodes.error.textContent = this.state.error;
      this.nodes.error.hidden = !this.state.error;
    }

    if (this.nodes.cta) {
      // Accessories alone are not a set: their price is the set's advantage, not an offer of its own.
      this.nodes.cta.disabled = bags === 0 || this.state.busy;
      this.nodes.cta.textContent = this.state.added ? this.t('added') : this.t('add_to_cart');
      this.nodes.cta.classList.toggle('is-added', this.state.added);
    }

    this.hasRendered = true;
  }

  /**
   * What each preset costs, on its card. Fixed for the life of the page — a preset is a set of
   * quantities, and neither those nor the prices change — so it is written once.
   */
  renderPresetPrices() {
    this.nodes.presets.forEach((card) => {
      const preset = this.presets[parseInt(card.dataset.presetIndex, 10)];
      if (!preset) return;

      const { now, was } = this.totals(this.qtyFromPreset(preset), true);
      const price = card.querySelector('[data-bb="preset-price"]');
      const compare = card.querySelector('[data-bb="preset-compare"]');
      if (price) price.textContent = this.formatMoney(now);
      if (compare) compare.textContent = was > now ? this.formatMoney(was) : '';
    });
  }

  /** The preset the current configuration is, or undefined for a mix of the buyer's own. */
  matchingPreset() {
    if (!this.state.accessory) return undefined;
    return this.presets.find((preset) =>
      this.sorts.every((sort) => (this.state.qty[sort.variantId] || 0) === (preset.qty[sort.variantId] || 0)),
    );
  }

  renderPresets() {
    const selected = this.matchingPreset();
    this.nodes.presets.forEach((card) => {
      const preset = this.presets[parseInt(card.dataset.presetIndex, 10)];
      if (!preset) return;

      const matches = preset === selected;
      card.classList.toggle('is-selected', matches);
      card.setAttribute('aria-pressed', matches ? 'true' : 'false');
    });
  }

  renderMeter(bags, rate) {
    const { tier2 = 0, tier3 = 0 } = this.config;

    // Only on the way up, and not for a preset that arrives at the top: the burst rewards
    // the buyer's last bag, and would be noise as a page-load animation.
    if (bags >= MAX_TIER_BAGS && !this.maxTierReached) this.celebrate();
    this.maxTierReached = bags >= MAX_TIER_BAGS;

    this.nodes.meterSegments.forEach((segment, index) => {
      segment.classList.toggle('is-on', bags >= index + 1);
      segment.classList.toggle('is-max', bags >= MAX_TIER_BAGS);
    });

    if (!this.nodes.meterTitle) return;

    let title = '';
    if (bags === 0) {
      title = this.t('meter_empty');
    } else if (bags === 1) {
      title = this.t('meter_to_tier2', { discount: tier2 });
    } else if (bags === 2) {
      const unlocksGift = Boolean(this.config.gift) && bags + 1 >= this.giftThreshold();
      title = this.t(unlocksGift ? 'meter_to_tier3_gift' : 'meter_to_tier3', { discount: tier3 });
    } else {
      title = this.t('meter_max');
    }

    this.nodes.meterTitle.textContent = title;

    // Below the top tier the place beside the title is the way to add a bag; at the top tier it
    // states the discount reached. Nothing to add (everything sold out or at its maximum) leaves
    // only the title.
    const atTop = bags >= MAX_TIER_BAGS;
    if (this.nodes.meterAdd) this.nodes.meterAdd.hidden = atTop || !this.canAddBag();

    const hint = this.nodes.meterHint;
    if (!hint) return;

    const discount = Math.round(rate * 100);
    hint.hidden = !atTop || rate <= 0;
    hint.textContent = hint.hidden ? '' : this.t('meter_applied', { discount });

    // On a narrow phone the two no longer share a line, and the title would wrap into a second
    // one. The figure alone says the same thing and fits.
    if (!hint.hidden) {
      const title = this.nodes.meterTitle;
      const lineHeight = parseFloat(getComputedStyle(title).lineHeight) || 20;
      if (title.offsetHeight > lineHeight * 1.5) hint.textContent = this.t('meter_applied_short', { discount });
    }
  }

  canAddBag() {
    const max = this.config.maxPerSort || 9;
    return this.sorts.some((sort) => sort.available && (this.state.qty[sort.variantId] || 0) < max);
  }

  /**
   * The meter's "+ Matcha": the bags are in the rows of the "Im Set" box, folded away on a phone.
   * Open them, bring them into view and point at the "+" buttons — focus goes to the first one,
   * so a keyboard is where a mouse is. Choosing which matcha stays with the buyer.
   */
  revealRows() {
    const panel = this.nodes.rowsPanel;
    if (!panel) return;

    const wasOpen = this.state.open;
    if (!wasOpen) {
      this.state.open = true;
      this.renderPanel();
    }

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const reveal = () => {
      panel.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'nearest' });
      const buttons = this.nodes.matchaRows
        .map((row) => row.querySelector('[data-bb="inc"]'))
        .filter((button) => button && !button.disabled && button.offsetParent !== null);
      if (buttons[0]) buttons[0].focus({ preventScroll: true });
      buttons.forEach((button) => {
        button.classList.remove('is-pulsing');
        void button.offsetWidth; // restart the animation on a second click
        button.classList.add('is-pulsing');
        button.addEventListener('animationend', () => button.classList.remove('is-pulsing'), { once: true });
      });
    };

    if (this._revealTimer) clearTimeout(this._revealTimer);
    if (wasOpen || reduceMotion) {
      reveal();
      return;
    }

    // Scrolling while the panel still grows would aim at its folded height, so wait for it —
    // with a timeout in case the transition never reports back.
    let done = false;
    const onEnd = (event) => {
      if (event.target === panel && event.propertyName === 'grid-template-rows') finish();
    };
    const finish = () => {
      if (done) return;
      done = true;
      panel.removeEventListener('transitionend', onEnd);
      clearTimeout(this._revealTimer);
      reveal();
    };
    panel.addEventListener('transitionend', onEnd);
    this._revealTimer = setTimeout(finish, 450);
  }

  /** The set in one line: "Basic · 2× Classic · Chasen & Löffel". */
  renderSummary() {
    if (!this.nodes.summary) return;

    const parts = this.sorts
      .filter((sort) => (this.state.qty[sort.variantId] || 0) > 0)
      .map((sort) => {
        const count = this.state.qty[sort.variantId];
        return `${count > 1 ? `${count}× ` : ''}${sort.short || sort.title}`;
      });
    if (this.state.accessory && this.config.accessory) parts.push(this.t('accessory_short'));

    this.nodes.summary.textContent = parts.join(' · ') || this.t('empty');
  }

  renderPanel() {
    const open = this.state.open;

    if (this.nodes.rowsPanel) {
      this.nodes.rowsPanel.dataset.open = open ? 'true' : 'false';
      this.nodes.rowsPanel.inert = !open;
    }

    if (this.nodes.customize) {
      this.nodes.customize.setAttribute('aria-expanded', open ? 'true' : 'false');
      this.nodes.customize.textContent = this.t(open ? 'done' : 'customize');
    }
  }

  renderAccessory() {
    const toggle = this.nodes.accToggle;
    if (!toggle) return;

    const on = this.state.accessory;
    const fixed = on && !this.accessoryRemovable();

    toggle.textContent = on ? this.t(fixed ? 'included' : 'remove') : this.t('add');
    toggle.classList.toggle('is-on', on);
    toggle.classList.toggle('is-fixed', fixed);
    if (this.nodes.accRow) this.nodes.accRow.classList.toggle('is-off', !on);
  }

  renderMatchaRows(rate) {
    this.nodes.matchaRows.forEach((row) => {
      const variantId = row.dataset.variantId;
      const sort = this.sorts.find((item) => String(item.variantId) === String(variantId));
      if (!sort) return;

      const count = this.state.qty[variantId] || 0;
      const discounted = sort.price - this.unitDiscount(sort.price, rate);

      const stepper = row.querySelector('[data-bb="stepper"]');
      const soldOut = row.querySelector('[data-bb="soldout"]');
      const countEl = row.querySelector('[data-bb="count"]');
      const priceEl = row.querySelector('[data-bb="unit-price"]');

      const unavailable = sort.available === false;
      row.classList.toggle('is-soldout', unavailable);

      if (soldOut) soldOut.hidden = !unavailable;
      if (stepper) stepper.hidden = unavailable;
      if (countEl) countEl.textContent = count;

      if (priceEl) {
        priceEl.textContent =
          count > 0 && rate > 0
            ? this.t('price_instead', { price: this.formatMoney(discounted), regular: this.formatMoney(sort.price) })
            : this.formatMoney(sort.price);
      }
    });
  }

  /**
   * The heading names the set that is selected: the preset's feed product title while the
   * configuration matches a preset, the generic set name for a mix of the buyer's own. The page
   * was rendered with the right title for its preset, so the first render leaves it alone unless a
   * link preselected another one — that keeps the feed bundle's title as the crawler read it.
   *
   * Titles of different lengths wrap to different numbers of lines, which would move everything
   * below the heading, the card the buyer just tapped included. Scrolling by the same amount keeps
   * the builder where it was.
   */
  renderPageTitle() {
    const heading = this.nodes.pageTitle;
    if (!heading || (!this.hasRendered && !this.deepLinked)) return;

    const preset = this.matchingPreset();
    const title = (preset && preset.title) || this.config.genericTitle;
    if (!title || heading.textContent === title) return;

    const anchor = this.nodes.widget;
    const before = anchor && this.hasRendered ? anchor.getBoundingClientRect().top : null;
    heading.textContent = title;
    if (before === null) return;

    const shift = anchor.getBoundingClientRect().top - before;
    if (shift) window.scrollBy({ top: shift, behavior: 'instant' });
  }

  /**
   * A short confetti burst over the meter when the last tier lands. Hand-rolled rather than a
   * library: it is one canvas, forty rectangles and a gravity constant, and it disappears with
   * itself when the last one falls out of the box.
   */
  celebrate() {
    const box = this.nodes.meterBox;
    if (!box) return;
    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    if (this._confettiFrame) cancelAnimationFrame(this._confettiFrame);
    if (this._confettiCanvas) this._confettiCanvas.remove();

    const canvas = document.createElement('canvas');
    canvas.className = 'bb-set-builder__confetti';
    canvas.setAttribute('aria-hidden', 'true');
    box.appendChild(canvas);
    this._confettiCanvas = canvas;

    const ratio = Math.min(window.devicePixelRatio || 1, 2);
    const width = box.clientWidth;
    const height = box.clientHeight;
    canvas.width = width * ratio;
    canvas.height = height * ratio;

    const context = canvas.getContext('2d');
    context.scale(ratio, ratio);

    const colours = ['#EB9251', '#C5D467', '#2E6460', '#FFFFFF'];
    const pieces = Array.from({ length: 42 }, (unused, index) => {
      const angle = (-Math.PI / 2) + (index / 42 - 0.5) * 1.9;
      const speed = 4.2 + Math.random() * 3;
      return {
        x: width / 2,
        y: height * 0.55,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed,
        w: 5 + Math.random() * 4,
        h: 3 + Math.random() * 3,
        rotation: Math.random() * Math.PI,
        spin: (Math.random() - 0.5) * 0.3,
        drift: Math.random() * Math.PI * 2,
        colour: colours[index % colours.length],
      };
    });

    // Driven by elapsed time, not by frame count: a backgrounded tab stops calling us, and a
    // frame-counted burst would resume minutes later instead of being over.
    const DURATION = 1800;
    const FADE_FROM = 1250;
    let started = null;
    let previous = null;

    const draw = (now) => {
      if (started === null) started = now;
      const elapsed = now - started;
      const step = Math.min((now - (previous === null ? now : previous)) / 16.7, 3);
      previous = now;

      context.clearRect(0, 0, width, height);
      context.globalAlpha = elapsed > FADE_FROM ? Math.max(0, 1 - (elapsed - FADE_FROM) / (DURATION - FADE_FROM)) : 1;

      pieces.forEach((piece) => {
        // Light enough to hang in the air for a moment rather than drop straight out of the box.
        piece.vy = Math.min(piece.vy + 0.17 * step, 3.4);
        piece.vx *= Math.pow(0.985, step);
        piece.drift += 0.09 * step;
        piece.x += (piece.vx + Math.sin(piece.drift) * 0.35) * step;
        piece.y += piece.vy * step;
        piece.rotation += piece.spin * step;

        context.save();
        context.translate(piece.x, piece.y);
        context.rotate(piece.rotation);
        context.fillStyle = piece.colour;
        context.fillRect(-piece.w / 2, -piece.h / 2, piece.w, piece.h);
        context.restore();
      });

      if (elapsed < DURATION) {
        this._confettiFrame = requestAnimationFrame(draw);
      } else {
        canvas.remove();
        this._confettiCanvas = null;
        this._confettiFrame = null;
      }
    };

    this._confettiFrame = requestAnimationFrame(draw);
  }

  // ---- cart ----

  buildItems() {
    const bundleId = `SET-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`.toUpperCase();
    const items = [];
    const accessory = this.state.accessory ? this.config.accessory : null;

    const properties = { _bb_set: bundleId };
    if (this.config.setLabel) properties[this.config.setLabelKey || 'Set'] = this.config.setLabel;

    // The cart needs one line to head the group. A wrapper product at 0.00 lets that line carry the
    // set's own name; without one the accessory heads it, and the cart is titled after the whisk.
    // Either way the components stay separate lines, which is what the collection's quantity
    // discounts attach to.
    const parentId = this.config.wrapperVariantId || (accessory && accessory.variantId) || null;

    const wrapperId = this.config.wrapperVariantId;

    // Whichever line heads the group carries the "Set: …" label the packing table reads.
    if (wrapperId) items.push({ id: wrapperId, quantity: 1, properties });

    if (accessory) {
      const item = { id: accessory.variantId, quantity: 1, properties: wrapperId ? { _bb_set: bundleId } : properties };
      if (wrapperId) item.parent_id = wrapperId;
      items.push(item);
    }

    this.sorts.forEach((sort) => {
      const count = this.state.qty[sort.variantId] || 0;
      if (!count) return;
      const item = { id: sort.variantId, quantity: count, properties: { _bb_set: bundleId } };
      if (parentId) item.parent_id = parentId;
      items.push(item);
    });

    // The meter promises the whisk at this many bags, so it comes along without being asked for.
    // One per cart: bb-cart-gift.js takes a second one out again.
    if (this.giftUnlocked()) {
      const item = { id: this.config.gift.variantId, quantity: 1, properties: { _bb_set: bundleId, _bb_gift: '1' } };
      if (parentId) item.parent_id = parentId;
      items.push(item);
    }

    return items;
  }

  async addToCart() {
    if (this.state.busy || this.bagCount() === 0) return;

    this.state.busy = true;
    this.state.error = '';
    if (this.nodes.cta) this.nodes.cta.setAttribute('aria-busy', 'true');
    this.render();

    const sections = ['variant-added'];
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:prepare-bundled-sections', { bubbles: true, detail: { sections } })
    );

    try {
      const response = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify({
          items: this.buildItems(),
          sections: sections.join(','),
          sections_url: window.location.pathname,
        }),
      });

      const json = await response.json();

      if (!response.ok) {
        this.state.error = json.description || json.message || this.t('generic_error');
        document.dispatchEvent(new CustomEvent('cart:refresh'));
        return;
      }

      const cart = await (await fetch(`${window.Shopify.routes.root}cart.js`)).json();
      cart.sections = json.sections;

      this.dispatchEvent(
        new CustomEvent('variant:add', {
          bubbles: true,
          detail: { items: json.items || [json], cart },
        })
      );
      document.documentElement.dispatchEvent(
        new CustomEvent('cart:change', { bubbles: true, detail: { baseEvent: 'variant:add', cart } })
      );

      this.state.added = true;
      if (this._addedTimer) clearTimeout(this._addedTimer);
      this._addedTimer = setTimeout(() => {
        this.state.added = false;
        this.render();
      }, 1800);
    } catch (error) {
      console.error('[bb-set-builder] add to cart failed', error);
      this.state.error = this.t('generic_error');
      document.dispatchEvent(new CustomEvent('cart:refresh'));
    } finally {
      this.state.busy = false;
      if (this.nodes.cta) this.nodes.cta.removeAttribute('aria-busy');
      this.render();
    }
  }
}

if (!window.customElements.get('bb-set-builder')) {
  window.customElements.define('bb-set-builder', BbSetBuilder);
}
